import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen text-black bg-white">
    <Seo title="About — 13+ Years Across Amazon, Flexport & UPS" description="Tare Labs is led by a solutions architect with 13+ years in supply chain and logistics technology at Amazon, Flexport, and UPS. EDI, integration, and AI for SMBs." path="/about" />
      <Header />

      {/* About Section */}
      <section id="about" className="py-20 px-6 relative z-30">
        <div className="container mx-auto">
          <h1 className="text-5xl font-black mb-20 text-center">
            Who You're Working With
          </h1>

          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="relative aspect-square">
              <div className="bg-black w-full h-full flex items-center justify-center shadow-lg">
                <img
                  src="/dan.jpg"
                  alt="Dan - Founder of Tare Labs"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 h-20 bg-white items-center shadow-lg border-4 border-white/80 rounded-md">
                <img
                  src="/t.png"
                  alt="Tare Labs"
                  className="h-full flex items-center justify-center"
                />
              </div>
            </div>

            <div className="space-y-6">
              <p className="text-lg text-gray-600 leading-relaxed">
                I've spent 13+ years doing hands-on integration architecture at Amazon,
                Flexport, and UPS — designing and delivering the enterprise systems that
                move billions of transactions a year.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                At Amazon I architected agentic AI solutions and led technical enablement
                for vendors across Amazon's inbound supply chain. At Flexport I cut
                implementation time in half with self-service tooling and reusable
                integration assets. At UPS I designed the integration architecture behind
                99.9% SLAs. The work spans the full supply chain stack: API and EDI
                integration, logistics and supply chain systems, and AI-driven automation.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Here's the pattern I kept seeing. The technology that makes those companies
                fast isn't exotic — it's well-designed integration, clean data, and
                automation pointed at the right problem. What separates them from a
                mid-market 3PL usually isn't sophistication. It's budget and headcount.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Tare Labs exists to close that gap: the same architecture and the same
                rigor, sized and priced for companies that don't have a hundred-person
                platform team. I translate complex requirements into systems that scale,
                and I explain them the same way to your engineers and your board.
              </p>
              <p className="text-lg font-bold text-black pt-2">
                — Dan, Founder
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-black mb-4">We Work In Your Stack</h2>
          <div className="max-w-4xl space-y-6 mb-16">
            <p className="text-xl text-gray-600 leading-relaxed">
              Most of our work happens in tools you already own and pay for. We're not a
              reseller, we carry no partner quotas, and we don't show up with a platform
              to sell you.
            </p>
            <p className="text-xl text-gray-600 leading-relaxed">
              Integration patterns don't change much between platforms — the hard part was
              never the tool. Whatever you're running today, whether that's a modern iPaaS,
              an RPA suite, a homegrown script on an FTP server, or nothing yet, we'll work
              in it and tell you honestly if it's worth replacing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                area: "Integration & iPaaS",
                desc: "EDI and API connectivity, trading partner onboarding, and the middleware that ties it together.",
              },
              {
                area: "Workflow & RPA",
                desc: "Automating the repetitive handoffs between systems that were never designed to talk to each other.",
              },
              {
                area: "AI & Document Processing",
                desc: "Agents, extraction, and classification pointed at the manual work that scales with your headcount.",
              },
              {
                area: "Cloud, Data & Observability",
                desc: "Where it runs, where the data lives, and how you find out when something breaks.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white p-6 hover:shadow-md transition-all duration-300"
              >
                <h3 className="font-bold text-black mb-3">{item.area}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
